import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DomainCustomerMapping,
  CreateDomainCustomerMappingDto,
  UpdateDomainCustomerMappingDto,
  CsvUploadResult,
  RevioSite,
  SiteMapping,
  DomainMappingResponse,
} from '../components/settings/domain-mapping-settings.types';

@Injectable({ providedIn: 'root' })
export class DomainMappingService {
  private readonly baseUrl = `${environment.voiceAPIConfig.uri}/billing/rev-io/domain-mappings`;

  constructor(private readonly http: HttpClient) {}

  getAll(): Observable<DomainCustomerMapping[]> {
    return this.http.get<DomainCustomerMapping[]>(this.baseUrl);
  }

  getAllForExport(): Observable<DomainCustomerMapping[]> {
    return this.http.get<DomainCustomerMapping[]>(`${this.baseUrl}/export`);
  }

  getOne(id: number): Observable<DomainCustomerMapping> {
    return this.http.get<DomainCustomerMapping>(`${this.baseUrl}/${id}`);
  }

  getByDomain(domain: string): Observable<DomainCustomerMapping | null> {
    return this.http.get<DomainCustomerMapping | null>(
      `${this.baseUrl}/domain/${domain}`
    );
  }

  getUnmapped(domains: string[]): Observable<string[]> {
    return this.http.post<string[]>(`${this.baseUrl}/unmapped`, { domains });
  }

  create(
    dto: CreateDomainCustomerMappingDto & { siteMappings?: SiteMapping[] }
  ): Observable<DomainMappingResponse> {
    return this.http.post<DomainMappingResponse>(this.baseUrl, dto);
  }

  update(
    id: number,
    dto: UpdateDomainCustomerMappingDto & { siteMappings?: SiteMapping[] }
  ): Observable<DomainMappingResponse> {
    return this.http.put<DomainMappingResponse>(`${this.baseUrl}/${id}`, dto);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  uploadCsv(file: File): Observable<CsvUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CsvUploadResult>(
      `${this.baseUrl}/upload-csv`,
      formData
    );
  }

  getCustomerSites(customerId: number): Observable<RevioSite[]> {
    return this.http.get<RevioSite[]>(
      `${environment.voiceAPIConfig.uri}/billing/customers/${customerId}/sites`
    );
  }

  checkSiteMapping(
    domain: string,
    netsapiensSiteName: string,
    excludeId?: number
  ): Observable<DomainCustomerMapping | null> {
    const params = new URLSearchParams();
    params.set('domain', domain);
    params.set('netsapiensSiteName', netsapiensSiteName);
    if (excludeId) {
      params.set('excludeId', excludeId.toString());
    }
    return this.http.get<DomainCustomerMapping | null>(
      `${this.baseUrl}/site-mapping/check?${params.toString()}`
    );
  }

  checkSiteMappingByCustomer(
    domain: string,
    customerId: number,
    customerName?: string,
    excludeId?: number
  ): Observable<DomainCustomerMapping | null> {
    const params = new URLSearchParams();
    params.set('domain', domain);
    params.set('customerId', customerId.toString());
    if (customerName) {
      params.set('customerName', customerName);
    }
    if (excludeId) {
      params.set('excludeId', excludeId.toString());
    }
    return this.http.get<DomainCustomerMapping | null>(
      `${this.baseUrl}/site-mapping/check-customer?${params.toString()}`
    );
  }

  getSiteMappingsForDomain(domain: string, customerId?: number): Observable<Array<{ revioCustomerId: number; netsapiensSiteName: string; customerName?: string }>> {
    const params = new URLSearchParams();
    if (customerId) {
      params.set('customerId', customerId.toString());
    }
    const queryString = params.toString();
    const url = `${this.baseUrl}/domain/${domain}/site-mappings${queryString ? `?${queryString}` : ''}`;
    return this.http.get<Array<{ revioCustomerId: number; netsapiensSiteName: string }>>(url);
  }
}
